import { useEffect, useState } from 'react';
import {
  BadgePercent,
  BarChart3,
  Coins,
  CreditCard,
  FileText,
  LayoutGrid,
  LogOut,
  Moon,
  Package,
  MoreHorizontal,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  Sun,
  Truck,
  X,
} from 'lucide-react';

import { cn } from './lib/cn';
import { useTheme } from './lib/theme';
import { useAuth } from './auth/AuthContext';
import { Login } from './pages/Login';
import { NoStores } from './pages/NoStores';
import { SET_PASSWORD_PATH, SetPassword } from './pages/SetPassword';
import { Overview } from './pages/Overview';
import { Sales } from './pages/Sales';
import { CashMovements } from './pages/CashMovements';
import { Purchases } from './pages/Purchases';
import { Catalogue } from './pages/Catalogue';
import { Credit } from './pages/Credit';
import { Promotions } from './pages/Promotions';
import { Access } from './pages/Access';
import { Documents } from './pages/Documents';
import { Settings } from './pages/Settings';
import { Genie } from './pages/Genie';
import { StoreSwitcher } from './components/StoreSwitcher';
import { BrandTile, Logotype } from './components/Wordmark';

const NAV = [
  { id: 'overview', label: 'Overview', short: 'Home', icon: LayoutGrid, Page: Overview },
  { id: 'sales', label: 'Sales', short: 'Sales', icon: BarChart3, Page: Sales },
  { id: 'purchases', label: 'Purchases', short: 'Buying', icon: Truck, Page: Purchases },
  { id: 'cash', label: 'Cash', short: 'Cash', icon: Coins, Page: CashMovements },
  { id: 'catalogue', label: 'Catalogue', short: 'Stock', icon: Package, Page: Catalogue },
  { id: 'credit', label: 'Credit', short: 'Credit', icon: CreditCard, Page: Credit },
  { id: 'promotions', label: 'Promotions', short: 'Promos', icon: BadgePercent, Page: Promotions },
  { id: 'documents', label: 'Documents', short: 'Docs', icon: FileText, Page: Documents },
  { id: 'access', label: 'Access', short: 'Access', icon: ShieldCheck, Page: Access },
  { id: 'settings', label: 'Settings', short: 'Settings', icon: SettingsIcon, Page: Settings },
] as const;

/**
 * Genie is deliberately not in NAV. It is not another report page — it has its
 * own full-height layout and sits below the divider in the sidebar, the way the
 * design separates "look at your figures" from "ask about them".
 */
const GENIE = { id: 'genie', label: 'Genie', short: 'Genie', icon: Sparkles } as const;

type NavId = (typeof NAV)[number]['id'] | 'genie';

/**
 * What the phone's bottom bar shows.
 *
 * Ten sections do not fit a bottom bar. At 390px they are 39px each, under the
 * 44px touch guideline the rest of this app goes to trouble to meet, and the
 * labels stop being readable well before that. So four go in the bar and the
 * rest go behind More, which is the convention every phone platform settled on
 * for the same reason.
 *
 * Genie holds the third slot rather than Cash. On a desk the sidebar shows
 * everything at once and the question is only where Genie *belongs*; on a phone
 * the bar is a claim about what the four things worth one tap are, and asking a
 * question is the one thing here that a till cannot already do. Cash is a ledger
 * you go and read, which survives the extra tap behind More.
 */
const PHONE_BAR: readonly NavId[] = ['overview', 'sales', 'genie', 'catalogue'];

/**
 * Which section a URL names.
 *
 * There is no router here — sections are state, and every path is served the
 * same index.html by the SPA fallback. So a notification opening `/cash` would
 * boot at Overview like any other cold start unless something reads the path,
 * which is what this does. Only the first segment is looked at: the app has no
 * nested routes, and a query string on the end is not part of the section.
 *
 * Returns null for a path that names nothing — `/`, or a stale link to a
 * section that no longer exists — and the caller falls back to Overview.
 */
function sectionFromUrl(url: string): NavId | null {
  const segment = url.split(/[?#]/)[0]?.split('/').filter(Boolean)[0];
  if (!segment) return null;
  if (segment === 'genie') return 'genie';
  return NAV.find((item) => item.id === segment)?.id ?? null;
}

export function App(): React.JSX.Element {
  const { status, stores } = useAuth();

  // Before the session check, not after it: an emailed link works the same
  // whether or not this browser happens to be signed in.
  if (window.location.pathname === SET_PASSWORD_PATH) return <SetPassword />;

  if (status === 'loading') return <BootSplash />;
  if (status === 'anonymous' || status === 'awaiting-mfa') return <Login />;
  if (stores.length === 0) return <NoStores />;

  return <Shell />;
}

function BootSplash(): React.JSX.Element {
  // The session cookie is httpOnly, so whether we are signed in is unknowable
  // until /auth/me answers. Deliberately near-blank: a skeleton dashboard here
  // would flash a layout the reader may not be entitled to see at all.
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper">
      <span className="sr-only">Loading</span>
      <span className="animate-pulse opacity-60">
        <BrandTile size={28} />
      </span>
    </div>
  );
}

function Shell(): React.JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  // The opening section comes from the URL, which is how a notification tap
  // arrives cold: the service worker calls openWindow('/cash') and the app is
  // launched at that path with no running window to tell.
  const [active, setActive] = useState<NavId>(
    () => sectionFromUrl(window.location.pathname) ?? 'overview',
  );
  const [moreOpen, setMoreOpen] = useState(false);

  const current = NAV.find((item) => item.id === active) ?? NAV[0];
  const Page = active === 'genie' ? Genie : current.Page;

  // Sections are swapped in place rather than routed, so the scroll position
  // persists across them. Without this, moving from a scrolled dashboard to the
  // catalogue drops you into the middle of the table, past its own heading and
  // filters, looking like the page rendered wrong.
  //
  // The address bar is kept in step with `replaceState` — no navigation, so
  // nothing reloads. It matters because iOS discards a backgrounded PWA freely:
  // without this, a phone that reloaded hours after a notification tap would
  // come back to whatever section that notification named rather than where the
  // reader actually was.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setMoreOpen(false);

    const path = active === 'overview' ? '/' : `/${active}`;
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', `${path}${window.location.search}`);
    }
  }, [active]);

  // The warm path. A notification tapped while the app is already open focuses
  // the existing window rather than opening a second one, so there is no cold
  // start to read the URL — the service worker posts the target across instead.
  // Nothing listened for that message until now, which is why every tap landed
  // on whichever section happened to be on screen.
  useEffect(() => {
    const container = navigator.serviceWorker;
    if (!container) return;

    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; url?: string } | null;
      if (data?.type !== 'NOTIFICATION_CLICK' || !data.url) return;

      const section = sectionFromUrl(data.url);
      if (section) setActive(section);
    };

    container.addEventListener('message', onMessage);
    return () => container.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className="min-h-dvh bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-rule bg-surface">
        {/* The brand band. Two pixels of neon is the whole of the identity's
            presence in the chrome — everything below it is a working console. */}
        <div className="qs-brand-rule h-0.5" aria-hidden />

        <div className="mx-auto flex h-12 w-full max-w-[1600px] items-center gap-3 px-4 md:px-6">
          <Logotype className="hidden sm:inline-block" />
          <span className="sm:hidden">
            <BrandTile size={20} />
          </span>

          <StoreSwitcher />

          <span className="ml-auto hidden text-2xs text-ink-faint sm:inline">{user?.email}</span>

          {/* `ml-auto` on the group as well as on the address, because below
              `sm` the address is not rendered — and with the only auto margin
              on a hidden element these two sat against the store switcher in
              the middle of the bar. On a phone they are the far corner, which
              is where a reader reaches for sign-out. */}
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={toggleTheme}
              className="qs-icon-button rounded p-1.5 text-ink-muted hover:bg-sunken hover:text-ink"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
            </button>

            <button
              type="button"
              onClick={() => void logout()}
              className="qs-icon-button rounded p-1.5 text-ink-muted hover:bg-sunken hover:text-ink"
              aria-label="Sign out"
            >
              <LogOut size={15} aria-hidden />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1600px]">
        {/* 3.125rem, not 3rem: the header is a 48px bar plus the 2px brand
            band above it, and a sidebar stuck 2px too high leaves a band that
            page content scrolls up through. */}
        <nav
          aria-label="Sections"
          className="sticky top-[3.125rem] hidden h-[calc(100dvh-3.125rem)] w-44 shrink-0 border-r border-rule px-2 py-4 md:block"
        >
          <ul className="space-y-0.5">
            {NAV.map(({ id, label, icon: Icon }) => (
              <li key={id}>
                <NavButton
                  active={active === id}
                  onClick={() => setActive(id)}
                  icon={<Icon size={15} aria-hidden />}
                  label={label}
                />
              </li>
            ))}
          </ul>

          <div className="mt-4 border-t border-rule pt-4">
            <NavButton
              active={active === GENIE.id}
              onClick={() => setActive(GENIE.id)}
              icon={<GENIE.icon size={15} aria-hidden />}
              label={GENIE.label}
            />
          </div>
        </nav>

        <main className="min-w-0 flex-1 px-4 pt-5 pb-24 md:px-6 md:pb-8">
          <Page />
        </main>
      </div>

      <PhoneNav
        active={active}
        onNavigate={(id) => setActive(id)}
        open={moreOpen}
        onToggleMore={setMoreOpen}
      />
    </div>
  );
}

/**
 * The phone's bottom bar, plus the sheet holding everything that does not fit.
 *
 * The More button reads as the current section whenever the section is inside
 * the sheet — otherwise choosing Promotions would leave every bar item unlit and
 * nothing on screen saying where you are.
 */
function PhoneNav({
  active,
  onNavigate,
  open,
  onToggleMore,
}: {
  active: NavId;
  onNavigate: (id: NavId) => void;
  open: boolean;
  onToggleMore: (open: boolean) => void;
}): React.JSX.Element {
  // Built from PHONE_BAR rather than by filtering NAV, so the bar keeps the
  // order written there and can hold Genie, which is not one of NAV's rows.
  const inBar = PHONE_BAR.map((id) => (id === GENIE.id ? GENIE : NAV.find((item) => item.id === id)!));
  const inSheet = NAV.filter((item) => !PHONE_BAR.includes(item.id));
  const activeIsHidden = !PHONE_BAR.includes(active);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 md:hidden" role="presentation">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => onToggleMore(false)}
            className="absolute inset-0 h-full w-full bg-ink/40 backdrop-blur-[1px]"
          />

          <div
            role="dialog"
            aria-label="More sections"
            className="absolute inset-x-0 bottom-0 rounded-t-lg border-t border-rule bg-surface pb-[calc(env(safe-area-inset-bottom)+4.5rem)] shadow-float"
          >
            <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
              <h2 className="qs-eyebrow">More</h2>
              <button
                type="button"
                onClick={() => onToggleMore(false)}
                aria-label="Close"
                className="qs-icon-button rounded p-1 text-ink-muted hover:text-ink"
              >
                <X size={15} aria-hidden />
              </button>
            </div>

            <ul className="grid grid-cols-2 gap-1 p-2">
              {inSheet.map(({ id, label, icon: Icon }) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => {
                      onNavigate(id);
                      onToggleMore(false);
                    }}
                    aria-current={active === id ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded px-3 py-3 text-left text-sm',
                      active === id
                        ? 'bg-accent-soft font-medium text-accent'
                        : 'text-ink-muted hover:bg-sunken hover:text-ink',
                    )}
                  >
                    <Icon size={16} aria-hidden />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <nav
        aria-label="Sections"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-rule bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="flex">
          {inBar.map(({ id, short, icon: Icon }) => (
            <li key={id} className="flex-1">
              <BarButton
                active={active === id}
                onClick={() => {
                  onToggleMore(false);
                  onNavigate(id);
                }}
                icon={<Icon size={17} aria-hidden />}
                label={short}
              />
            </li>
          ))}

          <li className="flex-1">
            <BarButton
              active={activeIsHidden}
              expanded={open}
              onClick={() => onToggleMore(!open)}
              icon={<MoreHorizontal size={17} aria-hidden />}
              label={activeIsHidden ? sectionName(active) : 'More'}
            />
          </li>
        </ul>
      </nav>
    </>
  );
}

function sectionName(id: NavId): string {
  if (id === 'genie') return 'Genie';
  return NAV.find((item) => item.id === id)?.short ?? 'More';
}

function BarButton({
  active,
  expanded,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  expanded?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
      className={cn(
        'relative flex w-full flex-col items-center gap-1 py-2 transition-colors',
        active ? 'text-accent' : 'text-ink-faint',
      )}
    >
      {active && (
        <span className="qs-brand-rule absolute inset-x-3 top-0 h-0.5 rounded-b" aria-hidden />
      )}
      {icon}
      <span className="text-2xs">{label}</span>
    </button>
  );
}

/**
 * One sidebar entry.
 *
 * The active row is marked twice over: a tinted ground and a two-pixel brand bar
 * on its leading edge. The bar is what makes the current section findable in
 * peripheral vision — a tint alone disappears against a light theme on a phone
 * screen outdoors, which is exactly where this app gets used.
 */
function NavButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex w-full items-center gap-2.5 rounded px-2.5 py-1.5 text-left text-sm transition-colors',
        active
          ? 'bg-accent-soft font-medium text-accent'
          : 'text-ink-muted hover:bg-sunken hover:text-ink',
      )}
    >
      {active && (
        <span className="qs-brand-rule absolute inset-y-1 left-0 w-0.5 rounded-full" aria-hidden />
      )}
      {icon}
      {label}
    </button>
  );
}
